import { createMemo, createResource, createSignal, For, Match, Show, Switch } from "solid-js";
import { msg } from "@lingui/core/macro";
import {
  eq,
  member as memberTable,
  organization as organizationTable,
  user as userTable,
  type VaultItemRecord,
  vault_item as vaultItemTable,
} from "@boveda/powersync";
import {
  buildCreateOrgVaultRequest,
  buildRotateOrgVaultRequest,
  crypto,
  generateId,
  getMyVaultMembershipQuery,
  getMyVaultMembershipsQuery,
  getOrgVaultsQuery,
  getVaultItemsQuery,
  type OrgMemberPublic,
  type ProtectedItem,
  queryKeys,
  type VaultMembershipRow,
} from "@boveda/shared";
import { createPowerSyncQuery } from "@boveda/shared/solid";
import { ArrowLeft, KeyRound, Lock, Plus, ShieldAlert, Users, Vault } from "@fleet/icons";
import { api } from "@/lib/api";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Label, TextField } from "@fleet/ui";
import { useI18n } from "@/providers/i18n-provider";
import { type DecryptedVaultItem, type OpenedOrgVault, useVault } from "../vault-context";
import { deriveItemList } from "../vault-state";
import { ItemForm } from "./item-form";
import { ItemDetail } from "./vault-page";

interface OrgVaultRow {
  id: string;
  organization_id: string | null;
  name_envelope: string | null;
  key_epoch: number | null;
}

interface OrgRow {
  id: string;
  name: string | null;
}

interface MemberRow {
  organization_id: string | null;
  role: string | null;
}

interface UserRow {
  id: string;
  name: string | null;
  email: string | null;
}

interface OrgVaultListEntry {
  readonly id: string;
  readonly orgId: string;
  /** Name decrypted client-side, or null if I (still) do not have access. */
  readonly name: string | null;
  readonly hasAccess: boolean;
}

type ViewMode =
  | { kind: "list" }
  | { kind: "create" }
  | { kind: "vault"; id: string }
  | { kind: "new-item"; id: string }
  | { kind: "edit-item"; id: string; item: DecryptedVaultItem }
  | { kind: "view"; id: string; item: DecryptedVaultItem }
  | { kind: "members"; id: string };

function coerceRole(role: string): OrgMemberPublic["role"] {
  return role === "owner" || role === "admin" ? role : "member";
}

/**
 * ORGANIZATION vaults (multi-user zero-knowledge): the vault key is distributed
 * sealed to each member's public key; this client opens it with the user's
 * private key and decrypts names and items LOCALLY. Creating, adding items, and
 * revoking+rotating live here; the server only sees opaque blobs.
 */
export function OrgVaultsPage() {
  const { t } = useI18n();
  const vault = useVault();
  const { db } = usePowerSyncContext();
  const [mode, setMode] = createSignal<ViewMode>({ kind: "list" });

  const orgVaultsQuery = createPowerSyncQuery<OrgVaultRow>({
    queryKey: () => queryKeys.vault.orgVaults(vault.userId()),
    queryFn: () => {
      const d = vault.drizzle();
      return d ? getOrgVaultsQuery(d) : null;
    },
    db,
    enabled: () => Boolean(vault.drizzle()),
  });

  // My memberships: ONLY as a reactivity source (the context opens the key).
  // When a membership arrives/changes through sync, names are decrypted again
  // without reloading.
  const membershipsQuery = createPowerSyncQuery<VaultMembershipRow>({
    queryKey: () => queryKeys.vault.membership(undefined, vault.userId()),
    queryFn: () => {
      const d = vault.drizzle();
      return d ? getMyVaultMembershipsQuery(d, vault.userId()) : null;
    },
    db,
    enabled: () => Boolean(vault.drizzle()) && Boolean(vault.userId()),
  });

  // Names decrypted client-side. If there is no membership yet (or I was
  // revoked), the entry has no access — the raw envelope is never shown.
  const [entries] = createResource(
    () => ({
      rows: orgVaultsQuery.data ?? [],
      membershipStamp: JSON.stringify(
        (membershipsQuery.data ?? []).map((m) => [m.vault_id, m.key_epoch]),
      ),
    }),
    async ({ rows }) => {
      const out: OrgVaultListEntry[] = [];
      for (const row of rows) {
        try {
          const opened = await vault.openOrgVault(row.id);
          const name = row.name_envelope
            ? await crypto.decryptVaultName(opened.key, row.id, row.name_envelope)
            : null;
          out.push({ id: row.id, orgId: row.organization_id ?? "", name, hasAccess: true });
        } catch {
          out.push({
            id: row.id,
            orgId: row.organization_id ?? "",
            name: null,
            hasAccess: false,
          });
        }
      }
      return out;
    },
  );

  const currentVaultId = createMemo(() => {
    const m = mode();
    return m.kind === "list" || m.kind === "create" ? null : m.id;
  });

  return (
    <div class="mx-auto w-full max-w-2xl px-4 py-6" onPointerDown={() => vault.keepAlive()}>
      <Switch>
        <Match when={mode().kind === "list"}>
          <div class="mb-4 flex items-center justify-between gap-3">
            <h1 class="text-xl font-semibold">{t(msg`Team vaults`)}</h1>
            <Button
              size="sm"
              onClick={() => setMode({ kind: "create" })}
              data-testid="org-vault-create"
            >
              <Plus class="h-4 w-4" /> {t(msg`New team vault`)}
            </Button>
          </div>

          <Show
            when={(entries() ?? []).length > 0}
            fallback={
              <EmptyState
                icon={Vault}
                title={t(msg`No team vaults yet`)}
                description={t(
                  msg`Create a vault for one of your organizations. Its key is sealed to each member's public key — the server can never open it.`,
                )}
              />
            }
          >
            <ul class="flex flex-col gap-1.5" data-testid="org-vault-list">
              <For each={entries() ?? []}>
                {(entry) => (
                  <li>
                    <button
                      type="button"
                      class="flex w-full items-center gap-3 rounded-lg border border-border/50 bg-card/40 px-3 py-2.5 text-left transition-colors hover:bg-primary/5"
                      onClick={() => setMode({ kind: "vault", id: entry.id })}
                      data-testid="org-vault-card"
                    >
                      <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                        <Show when={entry.hasAccess} fallback={<Lock class="h-4 w-4" />}>
                          <Vault class="h-4 w-4" />
                        </Show>
                      </span>
                      <span class="min-w-0 flex-1">
                        <span class="block truncate text-sm font-medium">
                          {entry.name ?? t(msg`Team vault`)}
                        </span>
                        <Show when={!entry.hasAccess}>
                          <span class="block truncate text-xs text-muted-foreground">
                            {t(msg`No access on this account`)}
                          </span>
                        </Show>
                      </span>
                    </button>
                  </li>
                )}
              </For>
            </ul>
          </Show>
        </Match>

        <Match when={mode().kind === "create"}>
          <BackHeader label={t(msg`New team vault`)} onBack={() => setMode({ kind: "list" })} />
          <CreateOrgVaultPanel onDone={() => setMode({ kind: "list" })} />
        </Match>

        <Match when={currentVaultId()}>
          {(vid) => (
            <OrgVaultDetail
              vaultId={vid()}
              entry={(entries() ?? []).find((e) => e.id === vid()) ?? null}
              mode={mode()}
              setMode={setMode}
            />
          )}
        </Match>
      </Switch>
    </div>
  );
}

function BackHeader(props: { label: string; onBack: () => void }) {
  return (
    <div class="mb-4 flex items-center gap-2">
      <Button variant="ghost" size="icon" aria-label={props.label} onClick={() => props.onBack()}>
        <ArrowLeft class="h-4 w-4" />
      </Button>
      <h1 class="text-lg font-semibold">{props.label}</h1>
    </div>
  );
}

/**
 * Create a team vault: requests the organization's members' PUBLIC keys from
 * the server, generates the vault key LOCALLY, seals it to each member, and
 * uploads only opaque blobs.
 */
function CreateOrgVaultPanel(props: { onDone: () => void }) {
  const { t } = useI18n();
  const vault = useVault();
  const { db } = usePowerSyncContext();
  const [selectedOrg, setSelectedOrg] = createSignal<string | null>(null);
  const [name, setName] = createSignal("");
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal("");

  const orgsQuery = createPowerSyncQuery<OrgRow>({
    queryKey: () => queryKeys.vault.localOrgs(vault.userId(), "create"),
    queryFn: () => {
      const d = vault.drizzle();
      return d
        ? d
            .select({ id: organizationTable.id, name: organizationTable.name })
            .from(organizationTable)
        : null;
    },
    db,
    enabled: () => Boolean(vault.drizzle()),
  });

  const myMembershipsQuery = createPowerSyncQuery<MemberRow>({
    queryKey: () => queryKeys.vault.myOrgRoles(vault.userId()),
    queryFn: () => {
      const d = vault.drizzle();
      return d
        ? d
            .select({ organization_id: memberTable.organization_id, role: memberTable.role })
            .from(memberTable)
            .where(eq(memberTable.user_id, vault.userId()))
        : null;
    },
    db,
    enabled: () => Boolean(vault.drizzle()) && Boolean(vault.userId()),
  });

  // Only an org owner/admin can create its vault (the server requires it).
  const adminOrgs = createMemo<OrgRow[]>(() => {
    const roles = new Map(
      (myMembershipsQuery.data ?? []).map((m) => [m.organization_id ?? "", m.role ?? ""]),
    );
    return (orgsQuery.data ?? []).filter((o) => {
      const role = roles.get(o.id);
      return role === "owner" || role === "admin";
    });
  });

  async function create(): Promise<void> {
    const orgId = selectedOrg();
    const vaultName = name().trim();
    if (!orgId || !vaultName) {
      setError(t(msg`Pick an organization and a name.`));
      return;
    }
    setBusy(true);
    setError("");
    try {
      const { data: keys, error: keysError } = await api.GET(
        "/api/vault/org/{org_id}/member-keys",
        { params: { path: { org_id: orgId } } },
      );
      if (keysError || !keys || keys.length === 0) {
        throw new Error("member keys unavailable");
      }
      const members: OrgMemberPublic[] = keys.map((k) => ({
        userId: k.userId,
        publicKey: k.publicKey,
        role: coerceRole(k.role),
      }));
      const { body } = await buildCreateOrgVaultRequest({
        vaultId: generateId(),
        organizationId: orgId,
        name: vaultName,
        members,
      });
      const { error: createError } = await api.POST("/api/vault/org", { body });
      if (createError) {
        throw new Error("create org vault failed");
      }
      props.onDone();
    } catch {
      setError(
        t(
          msg`Could not create the team vault. You need to be online and an admin of the organization.`,
        ),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="flex flex-col gap-4" data-testid="org-vault-create-panel">
      <p class="text-sm text-muted-foreground">
        {t(
          msg`The vault key is generated on this device and sealed to each member's public key. The server only stores opaque envelopes.`,
        )}
      </p>

      <Show
        when={adminOrgs().length > 0}
        fallback={
          <EmptyState
            icon={Users}
            title={t(msg`No organizations`)}
            description={t(msg`You need to be the owner or an admin of an organization first.`)}
          />
        }
      >
        <div class="flex flex-col gap-1.5">
          <span class="text-xs font-medium text-muted-foreground">{t(msg`Organization`)}</span>
          <ul class="flex flex-col gap-1.5">
            <For each={adminOrgs()}>
              {(org) => (
                <li>
                  <button
                    type="button"
                    class={`flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors ${
                      selectedOrg() === org.id
                        ? "border-primary bg-primary/10"
                        : "border-border/50 bg-card/40 hover:bg-primary/5"
                    }`}
                    onClick={() => setSelectedOrg(org.id)}
                    data-testid="org-vault-org-option"
                  >
                    <Users class="h-4 w-4 shrink-0 text-primary" />
                    <span class="truncate text-sm font-medium">{org.name}</span>
                  </button>
                </li>
              )}
            </For>
          </ul>
        </div>

        <div class="flex flex-col gap-1.5">
          <Label for="org-vault-name">{t(msg`Vault name`)}</Label>
          <TextField
            id="org-vault-name"
            placeholder={t(msg`e.g. Engineering`)}
            value={name()}
            onInput={(e) => setName(e.currentTarget.value)}
            data-testid="org-vault-name"
          />
        </div>

        <Button
          onClick={() => void create()}
          disabled={busy() || !selectedOrg() || !name().trim()}
          data-testid="org-vault-submit"
        >
          {busy() ? t(msg`Creating…`) : t(msg`Create team vault`)}
        </Button>
      </Show>

      <Show when={error()}>
        <p class="text-sm text-destructive" role="alert">
          {error()}
        </p>
      </Show>
    </div>
  );
}

/** Team vault details: client-decrypted items + management. */
function OrgVaultDetail(props: {
  vaultId: string;
  entry: OrgVaultListEntry | null;
  mode: ViewMode;
  setMode: (m: ViewMode) => void;
}) {
  const { t } = useI18n();
  const vault = useVault();
  const { db } = usePowerSyncContext();

  // MY membership in THIS vault as a reactive query: if it arrives through sync,
  // changes epoch (rotation), or disappears (revocation), the key reopens/closes
  // live without reloading.
  const membershipQuery = createPowerSyncQuery<VaultMembershipRow>({
    queryKey: () => queryKeys.vault.membership(props.vaultId, vault.userId()),
    queryFn: () => {
      const d = vault.drizzle();
      return d ? getMyVaultMembershipQuery(d, props.vaultId, vault.userId()) : null;
    },
    db,
    enabled: () => Boolean(vault.drizzle()) && Boolean(vault.userId()),
  });

  const [opened, { refetch: reopenKey }] = createResource(
    () => ({
      vid: props.vaultId,
      epoch: membershipQuery.data?.[0]?.key_epoch ?? null,
      present: (membershipQuery.data ?? []).length > 0,
      loading: membershipQuery.isLoading,
    }),
    async ({ vid, present, loading }): Promise<OpenedOrgVault | null> => {
      if (loading) {
        return null;
      }
      if (!present) {
        return null;
      }
      try {
        return await vault.openOrgVault(vid);
      } catch {
        return null;
      }
    },
  );

  const itemsQuery = createPowerSyncQuery<VaultItemRecord>({
    queryKey: () => queryKeys.vault.items(props.vaultId),
    queryFn: () => {
      const d = vault.drizzle();
      return d ? getVaultItemsQuery(d, props.vaultId) : null;
    },
    db,
    enabled: () => Boolean(vault.drizzle()),
  });

  const [decrypted] = createResource(
    () => ({ rows: itemsQuery.data ?? [], key: opened()?.key ?? null, epoch: opened()?.epoch }),
    async ({ rows, key }) => {
      if (!key) {
        return [] as DecryptedVaultItem[];
      }
      const out: DecryptedVaultItem[] = [];
      for (const row of rows) {
        try {
          out.push(await vault.decryptRowWith(key, row));
        } catch {
          // Different epoch / envelope not yet resynchronized: retry when it arrives.
        }
      }
      return out;
    },
  );

  const listItems = createMemo(() =>
    deriveItemList(
      (decrypted() ?? []).map((d) => ({
        id: d.id,
        type: d.plain.type,
        name: d.plain.name,
        username: d.plain.fields.username,
        updatedAt: d.plain.updatedAt,
      })),
    ),
  );
  const byId = createMemo(() => new Map((decrypted() ?? []).map((d) => [d.id, d])));
  const title = () => props.entry?.name ?? t(msg`Team vault`);
  const canAdmin = () => {
    const role = opened()?.role;
    return role === "owner" || role === "admin";
  };

  async function handleSave(
    plain: Parameters<typeof vault.saveItem>[0],
    existing?: { id: string; rev: number },
  ): Promise<void> {
    const key = opened()?.key;
    if (!key) {
      throw new Error("vault key unavailable");
    }
    await vault.saveItemWith(props.vaultId, key, plain, existing);
    props.setMode({ kind: "vault", id: props.vaultId });
  }

  async function handleDelete(id: string): Promise<void> {
    await vault.deleteItem(props.vaultId, id);
    props.setMode({ kind: "vault", id: props.vaultId });
  }

  return (
    <Switch>
      <Match when={props.mode.kind === "vault"}>
        <BackHeader label={title()} onBack={() => props.setMode({ kind: "list" })} />

        <Show
          when={opened() !== null || opened.loading || membershipQuery.isLoading}
          fallback={
            <div
              class="flex items-center gap-3 rounded-lg border border-border/50 bg-card/40 px-3 py-3 text-sm"
              data-testid="org-vault-no-access"
            >
              <ShieldAlert class="h-5 w-5 shrink-0 text-warning" />
              {t(
                msg`You have no key for this vault. Either it has not synced yet or your access was revoked (the key was rotated).`,
              )}
            </div>
          }
        >
          <div class="mb-4 flex items-center justify-between gap-2">
            <Show when={canAdmin()}>
              <Button
                size="sm"
                variant="outline"
                onClick={() => props.setMode({ kind: "members", id: props.vaultId })}
                data-testid="org-vault-members"
              >
                <Users class="h-4 w-4" /> {t(msg`Members`)}
              </Button>
            </Show>
            <Button
              size="sm"
              class="ml-auto"
              onClick={() => props.setMode({ kind: "new-item", id: props.vaultId })}
              data-testid="org-vault-add-item"
            >
              <Plus class="h-4 w-4" /> {t(msg`Add item`)}
            </Button>
          </div>

          <Show
            when={listItems().length > 0}
            fallback={
              <EmptyState
                icon={KeyRound}
                title={t(msg`No items yet`)}
                description={t(msg`Items added here are readable by every vault member.`)}
              />
            }
          >
            <ul class="flex flex-col gap-1.5" data-testid="org-vault-item-list">
              <For each={listItems()}>
                {(item) => (
                  <li>
                    <button
                      type="button"
                      class="flex w-full items-center gap-3 rounded-lg border border-border/50 bg-card/40 px-3 py-2.5 text-left transition-colors hover:bg-primary/5"
                      onClick={() => {
                        const full = byId().get(item.id);
                        if (full) {
                          props.setMode({ kind: "view", id: props.vaultId, item: full });
                        }
                      }}
                      data-testid="org-vault-item"
                    >
                      <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                        <KeyRound class="h-4 w-4" />
                      </span>
                      <span class="min-w-0 flex-1">
                        <span class="block truncate text-sm font-medium">{item.name}</span>
                        <Show when={item.username}>
                          <span class="block truncate text-xs text-muted-foreground">
                            {item.username}
                          </span>
                        </Show>
                      </span>
                    </button>
                  </li>
                )}
              </For>
            </ul>
          </Show>
        </Show>
      </Match>

      <Match when={props.mode.kind === "new-item"}>
        <BackHeader
          label={t(msg`New item`)}
          onBack={() => props.setMode({ kind: "vault", id: props.vaultId })}
        />
        <ItemForm
          onSave={handleSave}
          onCancel={() => props.setMode({ kind: "vault", id: props.vaultId })}
        />
      </Match>

      <Match when={props.mode.kind === "edit-item"}>
        {(_) => {
          const current = props.mode;
          if (current.kind !== "edit-item") {
            return null;
          }
          return (
            <>
              <BackHeader
                label={t(msg`Edit item`)}
                onBack={() =>
                  props.setMode({ kind: "view", id: props.vaultId, item: current.item })
                }
              />
              <ItemForm
                editing={current.item}
                onSave={handleSave}
                onCancel={() =>
                  props.setMode({ kind: "view", id: props.vaultId, item: current.item })
                }
              />
            </>
          );
        }}
      </Match>

      <Match when={props.mode.kind === "view"}>
        {(_) => {
          const current = props.mode;
          if (current.kind !== "view") {
            return null;
          }
          const fresh = () => byId().get(current.item.id) ?? current.item;
          return (
            <ItemDetail
              item={fresh()}
              vaultId={props.vaultId}
              onBack={() => props.setMode({ kind: "vault", id: props.vaultId })}
              onEdit={() => props.setMode({ kind: "edit-item", id: props.vaultId, item: fresh() })}
              onDelete={() => handleDelete(current.item.id)}
            />
          );
        }}
      </Match>

      <Match when={props.mode.kind === "members"}>
        <BackHeader
          label={t(msg`Vault members`)}
          onBack={() => props.setMode({ kind: "vault", id: props.vaultId })}
        />
        <OrgVaultMembersPanel
          vaultId={props.vaultId}
          orgId={props.entry?.orgId ?? ""}
          opened={opened() ?? null}
          onRotated={() => {
            void reopenKey();
            props.setMode({ kind: "vault", id: props.vaultId });
          }}
        />
      </Match>
    </Switch>
  );
}

/**
 * Vault members + REVOKE AND ROTATE: generate a new key (epoch+1) on this
 * client, distribute it ONLY to those who remain, and re-encrypt every item.
 * The revoked member keeps their old key but cannot open anything from the new epoch.
 */
function OrgVaultMembersPanel(props: {
  vaultId: string;
  orgId: string;
  opened: OpenedOrgVault | null;
  onRotated: () => void;
}) {
  const { t } = useI18n();
  const vault = useVault();
  const { db } = usePowerSyncContext();
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal("");
  const [confirming, setConfirming] = createSignal<string | null>(null);

  const [memberKeys, { refetch }] = createResource(
    () => props.orgId,
    async (orgId) => {
      const { data } = await api.GET("/api/vault/org/{org_id}/member-keys", {
        params: { path: { org_id: orgId } },
      });
      return data ?? [];
    },
  );

  const usersQuery = createPowerSyncQuery<UserRow>({
    queryKey: () => queryKeys.vault.memberNames(`org-${props.orgId}`),
    queryFn: () => {
      const d = vault.drizzle();
      return d
        ? d
            .select({ id: userTable.id, name: userTable.name, email: userTable.email })
            .from(userTable)
        : null;
    },
    db,
    enabled: () => Boolean(vault.drizzle()),
  });

  const displayName = (userId: string): string => {
    const u = (usersQuery.data ?? []).find((row) => row.id === userId);
    return u?.name || u?.email || userId;
  };

  async function revokeAndRotate(
    revokedUserId: string,
    opened: OpenedOrgVault | null,
  ): Promise<void> {
    const keys = memberKeys() ?? [];
    if (!opened) {
      return;
    }
    setBusy(true);
    setError("");
    try {
      const d = vault.drizzle();
      if (!d) {
        throw new Error("database not ready");
      }
      // ALL vault envelopes (including trash): if they are not re-encrypted,
      // they would be unreadable under the new key.
      const rows = (await d
        .select()
        .from(vaultItemTable)
        .where(eq(vaultItemTable.vault_id, props.vaultId))) as VaultItemRecord[];
      const items: ProtectedItem[] = rows.map((row) => ({
        id: row.id,
        vaultId: row.vault_id ?? props.vaultId,
        envelope: row.envelope ?? "",
        rev: row.rev ?? 0,
      }));
      const remaining: OrgMemberPublic[] = keys
        .filter((k) => k.userId !== revokedUserId)
        .map((k) => ({ userId: k.userId, publicKey: k.publicKey, role: coerceRole(k.role) }));
      const { body } = await buildRotateOrgVaultRequest({
        vaultId: props.vaultId,
        currentEpoch: opened.epoch,
        oldVaultKey: opened.key,
        remainingMembers: remaining,
        items,
      });
      const { error: rotateError } = await api.POST("/api/vault/org/rotate", { body });
      if (rotateError) {
        throw new Error("rotate failed");
      }
      await refetch();
      props.onRotated();
    } catch {
      setError(t(msg`Could not rotate the vault key. Try again.`));
    } finally {
      setBusy(false);
      setConfirming(null);
    }
  }

  return (
    <div class="flex flex-col gap-4" data-testid="org-vault-members-panel">
      <p class="text-sm text-muted-foreground">
        {t(
          msg`Revoking a member rotates the vault key: a new key is generated on this device, sealed only to the remaining members, and every item is re-encrypted. The server never sees any of it in the clear.`,
        )}
      </p>

      <ul class="flex flex-col gap-1.5" data-testid="org-vault-member-list">
        <For each={memberKeys() ?? []}>
          {(memberKey) => (
            <li
              class="flex items-center gap-3 rounded-lg border border-border/50 bg-card/40 px-3 py-2.5"
              data-testid="org-vault-member"
            >
              <Users class="h-4 w-4 shrink-0 text-primary" />
              <span class="min-w-0 flex-1">
                <span class="block truncate text-sm font-medium">
                  {displayName(memberKey.userId)}
                </span>
                <span class="block truncate text-xs text-muted-foreground">{memberKey.role}</span>
              </span>
              <Show when={memberKey.userId !== vault.userId()}>
                <Show
                  when={confirming() === memberKey.userId}
                  fallback={
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy()}
                      onClick={() => setConfirming(memberKey.userId)}
                      data-testid="org-vault-revoke"
                    >
                      {t(msg`Revoke`)}
                    </Button>
                  }
                >
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={busy()}
                    onClick={() => void revokeAndRotate(memberKey.userId, props.opened)}
                    data-testid="org-vault-revoke-confirm"
                  >
                    {busy() ? t(msg`Rotating…`) : t(msg`Confirm revoke & rotate`)}
                  </Button>
                </Show>
              </Show>
            </li>
          )}
        </For>
      </ul>

      <Show when={error()}>
        <p class="text-sm text-destructive" role="alert">
          {error()}
        </p>
      </Show>
    </div>
  );
}
